import { FINAL_Q, LADDER, RULES } from "./engine.js";
export const DEFAULT_TUNING = { beta: 2.0, mu: 0.6, passMin: 0.25, passMax: 0.4, walkRate: 0.12, boldness: 6, betaTop: 0.3 };
/** The skill weight for a rung. */
export const betaFor = (ratingPct, tuning) => (ratingPct <= 5 ? tuning.betaTop : tuning.beta);
const logistic = (x) => 1 / (1 + Math.exp(-x));
const logit = (p) => Math.log(p / (1 - p));
/** Standard normal points and weights (a fine grid is plenty for a smooth curve). */
const GRID = (() => {
    const pts = [];
    const step = 0.05;
    for (let s = -7; s <= 7; s += step)
        pts.push({ s, w: Math.exp(-(s * s) / 2) });
    const total = pts.reduce((n, p) => n + p.w, 0);
    return pts.map((p) => ({ s: p.s, w: p.w / total }));
})();
/** The average chance across the public for an offset a. */
function publicRate(a, beta) {
    return GRID.reduce((n, p) => n + p.w * logistic(a + beta * p.s), 0);
}
const offsets = new Map();
/**
 * The calibration correction: the offset a(p) at which the public average is
 * exactly p. Plain logit(p) would drift towards 50% (a 1% question would be
 * solved by 2–3% of players).
 */
export function offsetFor(p, beta) {
    const key = `${p}:${beta}`;
    const cached = offsets.get(key);
    if (cached !== undefined)
        return cached;
    const target = Math.min(0.999, Math.max(0.001, p));
    let lo = logit(target) - 20;
    let hi = logit(target) + 20;
    for (let i = 0; i < 80; i++) {
        const mid = (lo + hi) / 2;
        if (publicRate(mid, beta) < target)
            lo = mid;
        else
            hi = mid;
    }
    const a = (lo + hi) / 2;
    offsets.set(key, a);
    return a;
}
/** This seat's chance of getting a question rated `ratingPct` right. */
export function chanceRight(profile, ratingPct, tuning = DEFAULT_TUNING) {
    const beta = betaFor(ratingPct, tuning);
    return logistic(offsetFor(ratingPct / 100, beta) + beta * profile.skill);
}
/** A standard normal draw from a uniform source (Box–Muller). */
export function normal(rand) {
    const u = Math.max(1e-12, rand());
    const v = rand();
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}
export function newProfile(rand, tuning = DEFAULT_TUNING) {
    return {
        skill: tuning.mu + normal(rand),
        passThreshold: tuning.passMin + rand() * (tuning.passMax - tuning.passMin),
        risk: rand(),
    };
}
/**
 * What an AI does with an open question: pass, answer (right or wrong, after
 * a human-looking pause), or run out of time.
 */
export function aiMove(state, seat, profile, rand, tuning = DEFAULT_TUNING) {
    const rating = state.question?.rating ?? LADDER[state.q - 1] ?? 50;
    const chance = chanceRight(profile, rating, tuning);
    const hard = 1 - rating / 100;
    // Mostly 5–25 seconds; harder questions take longer.
    const afterMs = Math.round(Math.min(RULES.answerMs - 500, 4000 + (6000 + 14000 * hard) * (0.4 + rand())));
    // A few run out of time on the hard rungs.
    if (rand() < 0.02 * hard * hard)
        return { kind: "timeout" };
    const canPass = state.q >= RULES.passFrom && state.q <= RULES.passTo && !state.forFun && seat.passQ === null && seat.state === "active";
    // They sense their own odds, roughly.
    const sensed = Math.min(1, Math.max(0, chance + (rand() - 0.5) * 0.2));
    if (canPass && sensed < profile.passThreshold)
        return { kind: "pass", afterMs: Math.round(afterMs * 0.6) };
    return { kind: "answer", right: rand() < chance, afterMs };
}
/** At the walk-away offer: the cautious walk, and more so if they feel shaky. */
export function aiWalks(profile, rand, tuning = DEFAULT_TUNING) {
    return rand() < tuning.walkRate * 2 * (1 - profile.risk);
}
/** At the final offer: take a sure share, or chase the pot? */
export function aiFinalChoice(state, profile, tuning = DEFAULT_TUNING) {
    const fin = state.seats.filter((s) => s.state === "finalist").length;
    const share = RULES.shareFund / Math.max(1, fin);
    const p = chanceRight(profile, LADDER[FINAL_Q - 1], tuning);
    const shot = p * state.pot * (1 + tuning.boldness * profile.risk);
    return shot > share ? "play" : "share";
}
/** A wrong answer that looks human: a trap option if there is one, else a near guess. */
export function aiWrongAnswer(state, rand) {
    const q = state.question;
    if (!q)
        return "?";
    if (q.format === "mc" && q.options) {
        const right = q.answers[0]?.toUpperCase() ?? "A";
        const wrong = q.options.map((_, i) => "ABCDEFGH"[i]).filter((l) => l !== right);
        return wrong[Math.floor(rand() * wrong.length)] ?? "A";
    }
    if (q.format === "number") {
        const n = Number(q.answers[0]);
        return String(Number.isFinite(n) ? n + (rand() < 0.5 ? -1 : 1) * (1 + Math.floor(rand() * 3)) : 0);
    }
    if (q.format === "letter")
        return "ABCDEFGHIJKLMNOPQRSTUVWXYZ".replace(q.answers[0]?.toUpperCase() ?? "", "")[Math.floor(rand() * 25)];
    return "no idea";
}
//# sourceMappingURL=ai.js.map